import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, euro, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MultiSelect } from "@/components/MultiSelect";
import { useStickyTableScroll } from "@/hooks/useStickyTableScroll";
import { toast } from "sonner";
import { PlusIcon as Plus, TableIcon as TableIcon, KanbanIcon as Kanban, MagnifyingGlassIcon as MagnifyingGlass, MoonIcon as Moon, UsersThreeIcon as UsersThree, EyeIcon as Eye, EyeSlashIcon as EyeSlash, PaperclipIcon as Paperclip, WarningCircleIcon as WarningCircle, PackageIcon as Package, FunnelIcon as Funnel, HeadsetIcon as Headset } from "@phosphor-icons/react";

const MERCE_BADGE = {
  "Non verificata": "bg-slate-100 text-slate-600 border-slate-200",
  "Non presente": "bg-rose-100 text-rose-700 border-rose-200",
  "Ordinata": "bg-blue-100 text-blue-700 border-blue-200",
  "Parziale": "bg-amber-100 text-amber-700 border-amber-200",
  "Presente": "bg-emerald-100 text-emerald-700 border-emerald-200",
};
const merceAlert = (m) => m === "Non presente" || m === "Parziale";

export default function Lavorazioni() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [view, setView] = useState("tabella");
  const [items, setItems] = useState(null);
  const [cfg, setCfg] = useState({ skill: [], fasi: [], stati_merce: [] });
  const [tecnici, setTecnici] = useState([]);
  // filtri
  const [q, setQ] = useState("");
  const [fFase, setFFase] = useState([]);
  const [fMerce, setFMerce] = useState([]);
  const [fSkill, setFSkill] = useState([]);
  const [fRuolo, setFRuolo] = useState([]);
  const [fCommerciale, setFCommerciale] = useState("");
  const [fPernotto, setFPernotto] = useState("");
  const [fManov, setFManov] = useState("");
  const [fOpz, setFOpz] = useState([]);
  const [fVisibile, setFVisibile] = useState("");
  const [fConclusi, setFConclusi] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  // create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cQ, setCQ] = useState("");
  const [cResults, setCResults] = useState([]);
  const [form, setForm] = useState(null);

  const faseMap = useMemo(() => Object.fromEntries((cfg.fasi || []).map((f) => [f.key, f])), [cfg]);
  const toArr = (v) => Array.isArray(v) ? v : (v ? [v] : []);
  const RUOLO_OPTIONS = ["Junior", "Medium", "Senior"];
  const OPZ_OPTIONS = [{ value: "pernotto_esterno", label: "Pernotto esterno" }, { value: "manovalanza_esterna", label: "Manovalanza esterna" }, { value: "remoto", label: "Remoto" }, { value: "solo_fornitura", label: "Solo fornitura" }];

  // Persistenza filtri per singolo utente (namespaced)
  const prefsKey = user?.id ? `tecsuite:lavorazioni:filters:${user.id}` : null;
  const prefsLoadedRef = useRef(false);
  const skipPersistRef = useRef(false);
  useEffect(() => {
    if (!prefsKey || prefsLoadedRef.current) return;
    try {
      const s = JSON.parse(localStorage.getItem(prefsKey) || "null");
      if (s) {
        if (s.view) setView(s.view);
        if (typeof s.q === "string") setQ(s.q);
        if (Array.isArray(s.fFase)) setFFase(s.fFase);
        if (Array.isArray(s.fMerce)) setFMerce(s.fMerce);
        if (Array.isArray(s.fSkill)) setFSkill(s.fSkill);
        if (Array.isArray(s.fRuolo)) setFRuolo(s.fRuolo);
        if (typeof s.fCommerciale === "string") setFCommerciale(s.fCommerciale);
        if (typeof s.fPernotto === "string") setFPernotto(s.fPernotto);
        if (typeof s.fManov === "string") setFManov(s.fManov);
        if (Array.isArray(s.fOpz)) setFOpz(s.fOpz);
        if (typeof s.fVisibile === "string") setFVisibile(s.fVisibile);
        if (typeof s.fConclusi === "string") setFConclusi(s.fConclusi);
        if (typeof s.showCompleted === "boolean") setShowCompleted(s.showCompleted);
        if (typeof s.dataDa === "string") setDataDa(s.dataDa);
        if (typeof s.dataA === "string") setDataA(s.dataA);
      }
    } catch { /* noop */ }
    prefsLoadedRef.current = true;
  }, [prefsKey]);
  useEffect(() => {
    if (!prefsKey || !prefsLoadedRef.current) return;
    if (skipPersistRef.current) { skipPersistRef.current = false; try { localStorage.removeItem(prefsKey); } catch { /* noop */ } return; }
    try {
      localStorage.setItem(prefsKey, JSON.stringify({ view, q, fFase, fMerce, fSkill, fRuolo, fCommerciale, fPernotto, fManov, fOpz, fVisibile, fConclusi, dataDa, dataA, showCompleted }));
    } catch { /* noop */ }
  }, [prefsKey, view, q, fFase, fMerce, fSkill, fRuolo, fCommerciale, fPernotto, fManov, fOpz, fVisibile, fConclusi, dataDa, dataA, showCompleted]);

  const load = useCallback(() => {
    api.get("/lavorazioni").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  useEffect(() => {
    api.get("/lavorazioni/settings").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/tecnici").then(({ data }) => setTecnici([...data].sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {});
    load();
  }, [load]);

  useEffect(() => {
    if (!showCreate) return;
    const t = setTimeout(() => {
      if (cQ.trim().length < 2) { setCResults([]); return; }
      api.get("/clients", { params: { q: cQ } }).then(({ data }) => setCResults(data.slice(0, 8))).catch(() => setCResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [cQ, showCreate]);

  const openCreate = () => {
    setForm({ cliente_id: "", cliente_nome: "", titolo_lavorazione: "", data_conferma_ordine: new Date().toISOString().slice(0, 10),
      importo_vendita: "", descrizione_lavorazione: "", fase_lavorazione: (cfg.fasi[0] || {}).key || "in_attesa", stato_merce: cfg.stati_merce[0] || "Non verificata" });
    setCQ(""); setCResults([]); setShowCreate(true);
  };

  const create = async () => {
    if (!form.cliente_id) { toast.error("Seleziona un cliente"); return; }
    if (!form.titolo_lavorazione.trim()) { toast.error("Inserisci il titolo della lavorazione"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/lavorazioni", {
        cliente_id: form.cliente_id, titolo_lavorazione: form.titolo_lavorazione, data_conferma_ordine: form.data_conferma_ordine,
        importo_vendita: parseFloat(form.importo_vendita) || 0, descrizione_lavorazione: form.descrizione_lavorazione,
        fase_lavorazione: form.fase_lavorazione, stato_merce: form.stato_merce,
      });
      toast.success(`Lavorazione ${data.numero_lavorazione} creata`);
      setShowCreate(false);
      nav(`/lavorazioni/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore creazione"); }
    finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    if (!items) return [];
    const ql = q.trim().toLowerCase();
    return items.filter((x) => {
      if (!showCompleted && (x.fase_lavorazione === "completata" || x.fase_lavorazione === "fatturato") && !fFase.includes(x.fase_lavorazione)) return false;
      if (fFase.length && !fFase.includes(x.fase_lavorazione)) return false;
      if (fMerce.length && !fMerce.includes(x.stato_merce)) return false;
      const righe = x.righe_tecnici_previsti || [];
      if (fRuolo.length && !righe.some((r) => toArr(r.descrizione).some((d) => fRuolo.includes(d)))) return false;
      if (fSkill.length && !righe.some((r) => toArr(r.skill).some((sk) => fSkill.includes(sk)))) return false;
      if (fCommerciale && x.commerciale_id !== fCommerciale) return false;
      if (fPernotto && String(x.pernotto_esterno) !== fPernotto) return false;
      if (fManov && String(x.manovalanza_esterna) !== fManov) return false;
      if (fOpz.length && !fOpz.every((o) => !!x[o])) return false;
      if (fVisibile && String(x.visibile_area_cliente) !== fVisibile) return false;
      if (fConclusi && String(!!x.all_interventi_completed) !== fConclusi) return false;
      if (dataDa && (x.data_conferma_ordine || "") < dataDa) return false;
      if (dataA && (x.data_conferma_ordine || "") > dataA) return false;
      if (ql) {
        const hay = `${x.numero_lavorazione} ${x.cliente_nome} ${x.titolo_lavorazione} ${x.descrizione_lavorazione} ${x.merce_necessaria}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [items, q, fFase, fMerce, fSkill, fRuolo, fCommerciale, fPernotto, fManov, fOpz, fVisibile, fConclusi, dataDa, dataA, showCompleted]);

  const LAV_PAGE_SIZE = 50;
  const [lavPage, setLavPage] = useState(1);
  useEffect(() => { setLavPage(1); }, [q, fFase, fMerce, fSkill, fRuolo, fCommerciale, fPernotto, fManov, fOpz, fVisibile, fConclusi, dataDa, dataA, showCompleted]);
  const lavPages = Math.max(1, Math.ceil(filtered.length / LAV_PAGE_SIZE));
  const pagedItems = useMemo(() => filtered.slice((lavPage - 1) * LAV_PAGE_SIZE, (lavPage - 1) * LAV_PAGE_SIZE + LAV_PAGE_SIZE), [filtered, lavPage]);

  const resetFilters = () => {
    skipPersistRef.current = true;
    setQ(""); setFFase([]); setFMerce([]); setFSkill([]); setFRuolo([]); setFCommerciale(""); setFPernotto(""); setFManov(""); setFOpz([]); setFVisibile(""); setFConclusi(""); setDataDa(""); setDataA("");
    if (prefsKey) { try { localStorage.removeItem(prefsKey); } catch { /* noop */ } }
  };
  const activeCount = (fFase.length ? 1 : 0) + (fMerce.length ? 1 : 0) + (fSkill.length ? 1 : 0) + (fRuolo.length ? 1 : 0)
    + (fCommerciale ? 1 : 0) + (fOpz.length ? 1 : 0) + (fVisibile ? 1 : 0)
    + (fConclusi ? 1 : 0) + (dataDa ? 1 : 0) + (dataA ? 1 : 0);

  const faseBadge = (key) => {
    const f = faseMap[key];
    return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-medium border" style={{ backgroundColor: (f?.color || "#94a3b8") + "20", color: f?.color || "#64748b", borderColor: (f?.color || "#94a3b8") + "55" }} data-testid="fase-badge">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: f?.color || "#94a3b8" }} />{f?.label || key}
    </span>;
  };

  return (
    <InternalLayout>
      <div className="p-6 lg:p-10">
      <PageHeader title="Lavorazioni" subtitle="Lavorazioni vendute dal commerciale, programmate manualmente dal PM"
        actions={<Button onClick={openCreate} className="rounded-sm gap-2" data-testid="nuova-lavorazione-btn"><Plus size={16} /> Nuova lavorazione</Button>} />

      <div className="flex items-center gap-2 mb-5">
        <Button variant={view === "tabella" ? "default" : "outline"} size="sm" onClick={() => setView("tabella")} className="rounded-sm gap-2" data-testid="view-tabella-btn"><TableIcon size={15} /> Tabella</Button>
        <Button variant={view === "kanban" ? "default" : "outline"} size="sm" onClick={() => setView("kanban")} className="rounded-sm gap-2" data-testid="view-kanban-btn"><Kanban size={15} /> Kanban PM</Button>
        <Button variant={showCompleted ? "default" : "outline"} size="sm" onClick={() => setShowCompleted((v) => !v)} className="rounded-sm gap-2" data-testid="toggle-completate" title="Mostra/nascondi le lavorazioni completate">{showCompleted ? <Eye size={15} /> : <EyeSlash size={15} />} {showCompleted ? "Completate visibili" : "Completate nascoste"}</Button>
        <span className="text-sm text-muted-foreground ml-2" data-testid="lav-count">{filtered.length} lavorazioni{view === "tabella" && lavPages > 1 ? ` · pag. ${lavPage}/${lavPages}` : ""}</span>
        {view === "tabella" && lavPages > 1 && (
          <div className="flex items-center gap-1 ml-auto" data-testid="lav-pager">
            <Button variant="outline" size="sm" className="rounded-sm h-7 px-2.5" disabled={lavPage <= 1} onClick={() => setLavPage((p) => Math.max(1, p - 1))} data-testid="lav-prev-page">‹ Prec.</Button>
            <Button variant="outline" size="sm" className="rounded-sm h-7 px-2.5" disabled={lavPage >= lavPages} onClick={() => setLavPage((p) => p + 1)} data-testid="lav-next-page">Succ. ›</Button>
          </div>
        )}
      </div>

      {/* Filtri */}
      <Card className="rounded-sm p-3 mb-5 shadow-none">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca numero, cliente, titolo, merce…" className="pl-8 rounded-sm w-72" data-testid="search-input" />
          </div>

          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant={activeCount ? "default" : "outline"} size="sm" className="rounded-sm gap-1.5 relative" data-testid="filter-button">
                <Funnel size={15} weight={activeCount ? "fill" : "regular"} /> Filtri
                {activeCount > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/90 text-primary text-[11px] font-bold" data-testid="filter-active-badge">{activeCount}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[560px] rounded-sm p-4" align="start" data-testid="filter-panel">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Fase lavorazione</Label>
                  <div className="mt-1"><MultiSelect options={(cfg.fasi || []).map((f) => ({ value: f.key, label: f.label, color: f.color }))} value={fFase} onChange={setFFase} placeholder="Tutte le fasi" width="w-full" testid="filter-fase" /></div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Stato merce</Label>
                  <div className="mt-1"><MultiSelect options={(cfg.stati_merce || []).map((s) => ({ value: s, label: s }))} value={fMerce} onChange={setFMerce} placeholder="Tutti gli stati merce" width="w-full" testid="filter-merce" /></div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Descrizione / Ruolo</Label>
                  <div className="mt-1"><MultiSelect options={RUOLO_OPTIONS.map((s) => ({ value: s, label: s }))} value={fRuolo} onChange={setFRuolo} placeholder="Tutti i ruoli" width="w-full" testid="filter-ruolo" /></div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Skill</Label>
                  <div className="mt-1"><MultiSelect options={(cfg.skill || []).map((s) => ({ value: s, label: s }))} value={fSkill} onChange={setFSkill} placeholder="Tutte le skill" width="w-full" testid="filter-skill" /></div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Commerciale</Label>
                  <Select value={fCommerciale || "all"} onValueChange={(v) => setFCommerciale(v === "all" ? "" : v)}>
                    <SelectTrigger className="mt-1 rounded-sm bg-white h-9" data-testid="filter-commerciale"><SelectValue placeholder="Tutti i commerciali" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tutti i commerciali</SelectItem>{tecnici.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Interventi collegati</Label>
                  <Select value={fConclusi || "all"} onValueChange={(v) => setFConclusi(v === "all" ? "" : v)}>
                    <SelectTrigger className="mt-1 rounded-sm bg-white h-9" data-testid="filter-interventi-conclusi"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tutte le lavorazioni</SelectItem><SelectItem value="true">Con tutti gli interventi conclusi</SelectItem><SelectItem value="false">Con interventi ancora aperti</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Visibilità area cliente</Label>
                  <Select value={fVisibile || "all"} onValueChange={(v) => setFVisibile(v === "all" ? "" : v)}>
                    <SelectTrigger className="mt-1 rounded-sm bg-white h-9" data-testid="filter-visibile"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tutte</SelectItem><SelectItem value="true">Visibile in area cliente</SelectItem><SelectItem value="false">Non visibile</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Opzioni operative</Label>
                  <div className="mt-1"><MultiSelect options={OPZ_OPTIONS} value={fOpz} onChange={setFOpz} placeholder="Tutte le opzioni" width="w-full" testid="filter-opzioni" /></div>
                </div>
                <div><Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Conferma ordine dal</Label><Input type="date" value={dataDa} onChange={(e) => setDataDa(e.target.value)} className="mt-1 rounded-sm h-9" data-testid="filter-data-da" /></div>
                <div><Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Conferma ordine al</Label><Input type="date" value={dataA} onChange={(e) => setDataA(e.target.value)} className="mt-1 rounded-sm h-9" data-testid="filter-data-a" /></div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                <Button variant="ghost" size="sm" onClick={resetFilters} className="rounded-sm text-muted-foreground" data-testid="reset-filters-btn">Reset filtri</Button>
                <Button size="sm" onClick={() => setFilterOpen(false)} className="rounded-sm" data-testid="filter-apply">Chiudi</Button>
              </div>
            </PopoverContent>
          </Popover>

          {activeCount > 0 && <Button variant="ghost" size="sm" onClick={resetFilters} className="rounded-sm text-muted-foreground" data-testid="filter-clear-inline">Azzera</Button>}
        </div>
      </Card>

      {items === null ? <p className="text-sm text-muted-foreground">Caricamento…</p> :
        view === "tabella" ? <TabellaView items={pagedItems} faseBadge={faseBadge} nav={nav} tecnici={tecnici} /> :
          <KanbanView items={filtered} fasi={cfg.fasi} nav={nav} reload={load} />}

      {/* Dialog nuova lavorazione */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="create-dialog">
          <DialogHeader><DialogTitle>Nuova lavorazione</DialogTitle><DialogDescription>Collega la lavorazione a un cliente esistente e inserisci i dati principali.</DialogDescription></DialogHeader>
          {form && <div className="space-y-3">
            <div>
              <Label className="text-xs">Cliente *</Label>
              {form.cliente_id ? (
                <div className="flex items-center justify-between mt-1 p-2 rounded-sm border border-border bg-secondary/40" data-testid="selected-client">
                  <span className="text-sm font-medium">{form.cliente_nome}</span>
                  <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, cliente_id: "", cliente_nome: "" })} className="h-6 text-xs">Cambia</Button>
                </div>
              ) : (
                <div className="relative mt-1">
                  <Input value={cQ} onChange={(e) => setCQ(e.target.value)} placeholder="Cerca cliente (min 2 caratteri)…" className="rounded-sm" data-testid="client-search" />
                  {cResults.length > 0 && <div className="absolute z-20 mt-1 w-full bg-white border border-border rounded-sm shadow-lg max-h-52 overflow-auto">
                    {cResults.map((c) => <button key={c.id} onClick={() => { setForm({ ...form, cliente_id: c.id, cliente_nome: c.ragione_sociale }); setCResults([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary" data-testid={`client-opt-${c.id}`}>{c.ragione_sociale}</button>)}
                  </div>}
                </div>
              )}
            </div>
            <div><Label className="text-xs">Titolo lavorazione *</Label><Input value={form.titolo_lavorazione} onChange={(e) => setForm({ ...form, titolo_lavorazione: e.target.value })} className="mt-1 rounded-sm" data-testid="create-titolo" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Data conferma ordine *</Label><Input type="date" value={form.data_conferma_ordine} onChange={(e) => setForm({ ...form, data_conferma_ordine: e.target.value })} className="mt-1 rounded-sm" data-testid="create-data" /></div>
              <div><Label className="text-xs">Importo vendita (€)</Label><Input type="number" min="0" step="0.01" value={form.importo_vendita} onChange={(e) => setForm({ ...form, importo_vendita: e.target.value })} className="mt-1 rounded-sm" data-testid="create-importo" /></div>
            </div>
            <div><Label className="text-xs">Descrizione breve</Label><Textarea value={form.descrizione_lavorazione} onChange={(e) => setForm({ ...form, descrizione_lavorazione: e.target.value })} className="mt-1 rounded-sm" rows={2} data-testid="create-descrizione" /></div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm">Annulla</Button>
            <Button onClick={create} disabled={saving} className="rounded-sm" data-testid="create-submit-btn">{saving ? "Creazione…" : "Crea lavorazione"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </InternalLayout>
  );
}

function TabellaView({ items, faseBadge, nav }) {
  const LAV_COLS = [
    { key: "cliente", label: "Cliente", sort: (x) => x.cliente_nome || "", render: (x) => <span className="text-sm font-medium">{x.cliente_nome}</span> },
    { key: "numero", label: "Numero", sort: (x) => x.numero_lavorazione || "", render: (x) => <span className="font-mono text-xs font-medium">{x.numero_lavorazione}</span> },
    { key: "conferma", label: "Conferma", sort: (x) => x.data_conferma_ordine || "", render: (x) => <span className="text-xs whitespace-nowrap">{fmtDate(x.data_conferma_ordine)}</span> },
    { key: "commerciale", label: "Commerciale", sort: (x) => x.commerciale_nome || "", render: (x) => <span className="text-xs">{x.commerciale_nome || "—"}</span> },
    { key: "titolo", label: "Titolo", className: "max-w-[240px]", sort: (x) => x.titolo_lavorazione || "", render: (x) => (<div className="flex items-center gap-1.5"><span className="truncate">{x.titolo_lavorazione}</span>{x.all_interventi_completed ? <span title="Tutti gli interventi collegati risultano conclusi" className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-300 font-medium"><WarningCircle size={11} weight="fill" className="text-emerald-500" />Interventi conclusi</span> : x.interventi_parz_conclusi && <span title="Alcuni interventi collegati risultano conclusi" className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] bg-amber-50 text-amber-800 border border-amber-300 font-medium"><WarningCircle size={11} weight="fill" className="text-amber-500" />Interventi parzialmente conclusi</span>}</div>) },
    { key: "importo", label: "Importo", align: "right", sort: (x) => Number(x.importo_vendita) || 0, render: (x) => <span className="text-sm whitespace-nowrap">{euro(x.importo_vendita)}</span> },
    { key: "merce", label: "Merce", sort: (x) => x.stato_merce || "", render: (x) => <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] border ${MERCE_BADGE[x.stato_merce] || ""}`}>{merceAlert(x.stato_merce) && <WarningCircle size={11} weight="fill" />}{x.stato_merce}</span> },
    { key: "ruolo", label: "Ruolo", className: "max-w-[160px]", sort: (x) => (x.righe_tecnici_previsti || []).flatMap((r) => Array.isArray(r.descrizione) ? r.descrizione : (r.descrizione ? [r.descrizione] : [])).join(", "), render: (x) => { const ruoli = [...new Set((x.righe_tecnici_previsti || []).flatMap((r) => Array.isArray(r.descrizione) ? r.descrizione : (r.descrizione ? [r.descrizione] : [])))]; return <span className="line-clamp-1 text-xs">{ruoli.join(", ") || "—"}</span>; } },
    { key: "ore", label: "Ore", align: "right", sort: (x) => Number(x.ore_totali_complessive) || 0, render: (x) => <span className="text-sm whitespace-nowrap font-medium">{x.ore_totali_complessive || 0}h</span> },
    { key: "data_prev", label: "Data prev.", sort: (x) => x.data_prevista || "", render: (x) => <span className="text-xs whitespace-nowrap">{x.data_prevista ? fmtDate(x.data_prevista) : "—"}</span> },
    { key: "opz", label: "Opz.", sort: (x) => (x.pernotto_esterno ? 1 : 0) + (x.manovalanza_esterna ? 1 : 0) + (x.remoto ? 1 : 0), render: (x) => <div className="flex gap-1">{x.pernotto_esterno && <span title="Pernotto" className="text-indigo-500"><Moon size={14} weight="fill" /></span>}{x.manovalanza_esterna && <span title="Manovalanza esterna" className="text-orange-500"><UsersThree size={14} weight="fill" /></span>}{x.remoto && <span title="Remoto" className="text-sky-500"><Headset size={14} weight="fill" /></span>}</div> },
    { key: "fase", label: "Fase", sort: (x) => x.fase_lavorazione || "", render: (x) => faseBadge(x.fase_lavorazione) },
    { key: "visibile", label: "Portale", sort: (x) => (x.visibile_area_cliente ? 1 : 0), render: (x) => x.visibile_area_cliente ? <Eye size={15} className="text-emerald-500" /> : <EyeSlash size={15} className="text-muted-foreground/50" /> },
    { key: "allegati", label: "All.", sort: (x) => Number(x.allegati_count) || 0, render: (x) => x.allegati_count > 0 ? <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground"><Paperclip size={12} />{x.allegati_count}</span> : "—" },
    { key: "aggiornato", label: "Aggiorn.", sort: (x) => x.updated_at || "", render: (x) => <span className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtDate(x.updated_at)}</span> },
  ];
  const DEFAULT_W = { cliente: 180, numero: 110, conferma: 100, commerciale: 140, titolo: 240, importo: 110, merce: 120, ruolo: 160, ore: 70, data_prev: 110, opz: 80, fase: 130, visibile: 80, allegati: 70, aggiornato: 110 };
  const [colW, setColW] = useState(() => { try { return { ...DEFAULT_W, ...JSON.parse(localStorage.getItem("lav_col_w") || "{}") }; } catch { return DEFAULT_W; } });
  useEffect(() => { try { localStorage.setItem("lav_col_w", JSON.stringify(colW)); } catch { /* noop */ } }, [colW]);
  const resizing = useRef(null);
  const startResize = (e, key) => {
    e.stopPropagation(); e.preventDefault();
    resizing.current = { key, startX: e.clientX, startW: colW[key] || DEFAULT_W[key] || 120 };
    const onMove = (ev) => { if (!resizing.current) return; const d = ev.clientX - resizing.current.startX; setColW((w) => ({ ...w, [resizing.current.key]: Math.max(50, resizing.current.startW + d) })); };
    const onUp = () => { resizing.current = null; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  };
  const totalW = LAV_COLS.reduce((s, c) => s + (colW[c.key] || DEFAULT_W[c.key] || 120), 0);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toggle = (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));
  const scrollRef = useStickyTableScroll();
  const colByKey = Object.fromEntries(LAV_COLS.map((c) => [c.key, c]));
  const sorted = (() => {
    if (!sort.key || !colByKey[sort.key]) return items;
    const acc = colByKey[sort.key].sort;
    const arr = [...items];
    arr.sort((a, b) => {
      const va = acc(a), vb = acc(b);
      let cmp;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), "it", { numeric: true, sensitivity: "base" });
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  })();

  return (
    <Card className="rounded-sm shadow-none overflow-hidden">
      <div ref={scrollRef}>
        <Table style={{ tableLayout: "fixed", width: totalW }}>
          <TableHeader>
            <TableRow>
              {LAV_COLS.map((c) => (
                <TableHead
                  key={c.key}
                  onClick={() => toggle(c.key)}
                  style={{ width: colW[c.key] || DEFAULT_W[c.key] }}
                  className={`relative cursor-pointer select-none hover:text-foreground ${c.align === "right" ? "text-right" : ""}`}
                  data-testid={`lavsort-${c.key}`}
                >
                  <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : ""}`}>
                    {c.label}
                    <span className="text-[10px] text-muted-foreground w-2">{sort.key === c.key ? (sort.dir === "asc" ? "▲" : "▼") : ""}</span>
                  </span>
                  <span
                    onMouseDown={(e) => startResize(e, c.key)}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60"
                    data-testid={`lavresize-${c.key}`}
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={LAV_COLS.length} className="text-center text-muted-foreground py-8">Nessuna lavorazione</TableCell></TableRow>}
            {sorted.map((x) => (
              <TableRow key={x.id} onClick={() => nav(`/lavorazioni/${x.id}`)} className={`cursor-pointer ${merceAlert(x.stato_merce) ? "bg-rose-50/40" : ""}`} data-testid={`lav-row-${x.id}`}>
                {LAV_COLS.map((c) => (
                  <TableCell key={c.key} style={{ width: colW[c.key] || DEFAULT_W[c.key] }} className={`overflow-hidden ${c.align === "right" ? "text-right" : ""} ${c.className || ""}`}>{c.render(x)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function KanbanView({ items, fasi, nav, reload }) {
  const [dragId, setDragId] = useState(null);
  const cols = (fasi || []).filter((f) => f.attivo !== false);
  const drop = async (faseKey) => {
    if (!dragId) return;
    const lav = items.find((x) => x.id === dragId);
    setDragId(null);
    if (!lav || lav.fase_lavorazione === faseKey) return;
    try {
      await api.put(`/lavorazioni/${lav.id}`, { ...lav, fase_lavorazione: faseKey });
      toast.success("Fase aggiornata");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };
  return (
    <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
      {cols.map((f) => {
        const colItems = items.filter((x) => x.fase_lavorazione === f.key);
        return (
          <div key={f.key} className="flex-shrink-0 w-72" onDragOver={(e) => e.preventDefault()} onDrop={() => drop(f.key)} data-testid={`kanban-col-${f.key}`}>
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: f.color }} />
              <span className="text-sm font-semibold">{f.label}</span>
              <span className="text-xs text-muted-foreground ml-auto">{colItems.length}</span>
            </div>
            <div className="space-y-2 min-h-[120px] rounded-sm p-1" style={{ backgroundColor: f.color + "0d" }}>
              {colItems.map((x) => (
                <Card key={x.id} draggable onDragStart={() => setDragId(x.id)} onClick={() => nav(`/lavorazioni/${x.id}`)}
                  className={`rounded-sm p-3 shadow-none cursor-pointer hover:shadow-md transition-shadow border-l-4 ${merceAlert(x.stato_merce) ? "bg-rose-50/40" : ""}`}
                  style={{ borderLeftColor: f.color }} data-testid={`kanban-card-${x.id}`}>
                  <div className="text-[11px] font-mono text-muted-foreground">{x.numero_lavorazione}</div>
                  <div className="text-sm font-semibold mt-0.5">{x.cliente_nome}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{x.titolo_lavorazione}</div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] border ${MERCE_BADGE[x.stato_merce] || ""}`}><Package size={10} />{x.stato_merce}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-secondary border border-border font-medium">{x.ore_totali_complessive || 0}h</span>
                    {x.data_prevista && <span className="text-[10px] text-muted-foreground">{fmtDate(x.data_prevista)}</span>}
                    {x.all_interventi_completed ? <span title='Tutti gli interventi collegati risultano conclusi' className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-300 font-medium" data-testid={`lav-interventi-conclusi-kanban-${x.id}`}><WarningCircle size={10} weight="fill" className="text-emerald-500" />Interventi conclusi</span> : x.interventi_parz_conclusi && <span title='Alcuni interventi collegati risultano conclusi' className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] bg-amber-50 text-amber-800 border border-amber-300 font-medium" data-testid={`lav-interventi-parz-kanban-${x.id}`}><WarningCircle size={10} weight="fill" className="text-amber-500" />Interventi parzialmente conclusi</span>}
                  </div>
                  {(x.skill_richieste || []).length > 0 && <div className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{x.skill_richieste.join(" · ")}</div>}
                  <div className="flex items-center gap-2 mt-2">
                    {x.pernotto_esterno && <span title="Pernotto" className="text-indigo-500"><Moon size={13} weight="fill" /></span>}
                    {x.manovalanza_esterna && <span title="Manovalanza esterna" className="text-orange-500"><UsersThree size={13} weight="fill" /></span>}
                    {x.remoto && <span title="Remoto" className="text-sky-500"><Headset size={13} weight="fill" /></span>}
                    {x.sottolavorazioni_count > 0 && <span className="text-[10px] text-muted-foreground">{x.sottolavorazioni_count} sotto-lav.</span>}
                    <span className="ml-auto" title={x.allegati_count > 0 ? "Conferma ordine presente" : "Nessun allegato"}>
                      <Paperclip size={13} className={x.allegati_count > 0 ? "text-emerald-600" : "text-muted-foreground/40"} />
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
